// SPDX-License-Identifier: GPL-3.0-or-later
// Copyright (C) 2026 Breach Contributors

import { useCallback, useEffect, useState } from 'react';
import { useStore } from '@/store';
import {
  activateAudit,
  deactivateAudit,
  getAuditLogs,
  getAuditStats,
  subscribeAudit,
} from '@/lib/audit-interceptor';

export default function PrivacySettings() {
  const { auditMode, setAuditMode } = useStore();
  const [logs, setLogs] = useState<ReturnType<typeof getAuditLogs>>([]);
  const [stats, setStats] = useState(getAuditStats());
  const [checksumStatus, setChecksumStatus] = useState<'unchecked' | 'match' | 'mismatch'>('unchecked');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (!auditMode) return;
    const unsub = subscribeAudit(() => {
      setLogs(getAuditLogs());
      setStats(getAuditStats());
    });
    return unsub;
  }, [auditMode]);

  const toggleAudit = useCallback(() => {
    const next = !auditMode;
    setAuditMode(next);
    if (next) {
      activateAudit();
    } else {
      deactivateAudit();
    }
  }, [auditMode, setAuditMode]);

  const verifyBuild = useCallback(async () => {
    setVerifying(true);
    try {
      const res = await fetch('/checksums.json', { cache: 'no-store' });
      if (!res.ok) throw new Error('Missing checksums');
      const checksums: Record<string, string> = await res.json();

      const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'));
      const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'));
      const assets = [
        ...scripts.map((s) => s.src.replace(location.origin, '')),
        ...links.map((l) => l.href.replace(location.origin, '')),
      ];

      const mismatches: string[] = [];
      for (const src of assets) {
        if (!checksums[src]) continue;
        const r = await fetch(src, { cache: 'force-cache' });
        const buf = await r.arrayBuffer();
        const hashBuf = await crypto.subtle.digest('SHA-256', buf);
        const hash = Array.from(new Uint8Array(hashBuf))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('');
        if (hash !== checksums[src]) {
          mismatches.push(src);
        }
      }

      setChecksumStatus(mismatches.length === 0 && assets.length > 0 ? 'match' : 'mismatch');
    } catch {
      setChecksumStatus('mismatch');
    } finally {
      setVerifying(false);
    }
  }, []);

  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-xl font-semibold">Privacy Audit Mode</h2>
        <p className="mt-1 text-sm text-neutral-400">
          Intercepts all network requests during file processing and proves zero outbound data
          egress.
        </p>
        <label className="mt-4 inline-flex cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            checked={auditMode}
            onChange={toggleAudit}
            className="h-5 w-5 accent-white"
          />
          <span className="font-medium">{auditMode ? 'Active' : 'Inactive'}</span>
        </label>

        {auditMode && (
          <div className="mt-4 rounded-lg border border-neutral-700 bg-breach-800 p-4">
            <div className="flex gap-6 text-sm">
              <div>
                Total: <span className="font-mono font-bold">{stats.total}</span>
              </div>
              <div>
                Outbound:{" "}
                <span className="font-mono font-bold text-red-400">{stats.outbound}</span>
              </div>
              <div>
                Same-Origin:{" "}
                <span className="font-mono font-bold text-green-400">{stats.sameOrigin}</span>
              </div>
            </div>
            <div className="mt-2 max-h-40 overflow-auto font-mono text-xs text-neutral-400">
              {logs.length === 0 && <p>No network activity detected.</p>}
              {logs.map((log, i) => (
                <div
                  key={i}
                  className={log.sameOrigin ? 'text-green-300' : 'text-red-300'}
                >
                  [{new Date(log.timestamp).toLocaleTimeString()}] {log.type.toUpperCase()}{' '}
                  {log.method} {log.url}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section>
        <h2 className="text-xl font-semibold">Build Verification</h2>
        <p className="mt-1 text-sm text-neutral-400">
          Compute SHA-256 checksums of the current bundle and compare against published
          checksums.json.
        </p>
        <button
          onClick={verifyBuild}
          disabled={verifying}
          className="mt-4 rounded-md bg-breach-100 px-4 py-2 text-sm font-medium text-breach-900 hover:bg-white disabled:opacity-50"
        >
          {verifying ? 'Verifying...' : 'Verify Build'}
        </button>
        {checksumStatus !== 'unchecked' && (
          <p
            className={`mt-2 text-sm font-medium ${
              checksumStatus === 'match' ? 'text-green-400' : 'text-red-400'
            }`}
          >
            {checksumStatus === 'match'
              ? '✅ Match — Bundle integrity verified'
              : '❌ Tampered — Checksums do not match'}
          </p>
        )}
      </section>

      <section>
        <h2 className="text-xl font-semibold">Telemetry &amp; Tracking</h2>
        <p className="mt-1 text-sm text-neutral-400">
          Tracking is disabled by architecture. It cannot be enabled.
        </p>
        <div className="mt-4 inline-flex items-center gap-3 opacity-50">
          <input type="checkbox" disabled checked={false} className="h-5 w-5" />
          <span className="font-medium">Send usage data</span>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold">Attribution</h2>
        <div className="mt-2 space-y-1 text-sm text-neutral-400">
          <p>Built with open-source software.</p>
          <a
            className="block underline hover:text-white"
            href="https://github.com/breach"
            target="_blank"
            rel="noreferrer"
          >
            Source Code on GitHub
          </a>
          <a className="block underline hover:text-white" href="/LICENSE" target="_blank">
            GNU General Public License v3.0
          </a>
        </div>
      </section>
    </div>
  );
}
